import React, { Component } from "react";

import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import moment from "moment-timezone";

import { PieChart, Pie, Cell } from "recharts";

import {
  selectFormat,
  getMetricForColumn,
  Error,
  selectIcon,
} from "components/Utils/Utils.js";

import Heading from "components/Elements/Heading";

const COLORS = ["#f87c5b", "#f4f4f4"];

const SourceFormatter = (column) => {
  const icon = column.toLowerCase();
  return (
    <>
      <img src={selectIcon(icon)} alt={icon} width="20" height="20" /> &nbsp;
      &nbsp;
      {column}
    </>
  );
};

const renderPieChart = (data) => {
  const chartData = [
    { value: 100 - (100 - data * 100) },
    { value: 100 - data * 100 },
  ];

  return (
    <PieChart height={45} width={45}>
      <Pie
        data={chartData}
        dataKey="value"
        outerRadius="110%"
        innerRadius="75%"
        startAngle={90}
        endAngle={-270}
      >
        {chartData.map((entry, index) => (
          <Cell key={`cell-${entry}`} fill={COLORS[index]} />
        ))}
      </Pie>
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="middle"
        className="piechart-metric__xsmall"
      >
        {selectFormat("percentage", data)}
      </text>
    </PieChart>
  );
};

class SocialSummary extends Component {
  componentDidMount() {
    this.props.childRef(this);
  }

  renderTable(data) {
    const { cells, title } = this.props;

    // add unique ids
    data.forEach((d, i) => (d.id = i));

    const total_index = data.findIndex(({ source }) => source === "Total");
    let table_data = data.slice(0);
    const footer_data = table_data.splice(total_index, 1)[0];

    const columns = [
      {
        dataField: "source",
        text: "Source",
        headerClasses: "sticky-column",
        classes: "sticky-column",
        style: { width: "140px", minWidth: "140px", textAlign: "left" },
        headerAlign: "left",
        formatter: SourceFormatter,
        csvFormatter: (cell, row) => cell,
        sort: true,
        footer: "Total",
      },
      {
        dataField: "posts",
        text: "Total Posts",
        style: { width: "110px", minWidth: "110px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,00"),
        headerAlign: "left",
        csvExport: true,
        footer: footer_data["posts"],
        footerFormatter: (col) => getMetricForColumn(col.footer),
      },
      {
        dataField: "reach",
        text: "Total Reach",
        style: { width: "110px", minWidth: "100px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,00"),
        csvExport: true,
        footer: footer_data["reach"],
        footerFormatter: (col) => getMetricForColumn(col.footer),
      },

      {
        dataField: "views",
        text: "Total Views",
        style: { width: "110px", minWidth: "110px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,00"),
        csvExport: true,
        footer: footer_data["views"],
        footerFormatter: (col) => getMetricForColumn(col.footer),
      },
      {
        dataField: "link_clicks",
        text: "Total Link Clicks",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,00"),
        csvExport: true,
        footer: footer_data["link_clicks"],
        footerFormatter: (col) => getMetricForColumn(col.footer),
      },
      {
        dataField: "followers_new",
        text: "Net New Followers",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,00"),
        csvExport: true,
        footer: footer_data["followers_new"],
        footerFormatter: (col) => getMetricForColumn(col.footer),
      },
      {
        dataField: "followers_percent",
        text: "",
        style: { width: "100px", minWidth: "100px" },
        formatter: renderPieChart,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell, "0.0%"),
        footer: footer_data["followers_percent"],
        footerFormatter: "",
      },
    ];

    let selected_columns = columns;
    if (cells && cells.length) {
      selected_columns = [];
      cells.forEach((cell, i) => {
        selected_columns.push(columns.find((o) => o.dataField === cell));
      });
    }

    const defaultSorted = [
      {
        dataField: "published_date",
        order: "desc",
      },
    ];

    if (cells && cells.length) {
      selected_columns = [];
      cells.forEach((cell, i) => {
        selected_columns.push(columns.find((o) => o.dataField === cell));
      });
    }

    const filename = title.split(" ").join("_") || "spreadsheet";

    return (
      <ToolkitProvider
        keyField="id"
        data={table_data}
        columns={selected_columns}
        striped={false}
        hover={false}
        bordered={false}
        exportCSV={{
          fileName: filename + ".csv",
          exportAll: false,
          onlyExportFiltered: true,
        }}
      >
        {(props) => (
          <BootstrapTable
            {...props.baseProps}
            {...props.csvProps}
            defaultSorted={defaultSorted}
            footerClasses={"tfoot__tr"}
            ref={(el) => (this.childRef = el)}
          />
        )}
      </ToolkitProvider>
    );
  }

  builtInExportCSV = () => this.childRef;

  render() {
    const { data } = this.props;

    const lastUpdated = moment
      .tz(data.now, data.cutoffTimezone)
      .format("dddd D MMM, HH:SS");
    const cutoffTime = moment
      .tz(data.cutoffTime, data.cutoffTimezone)
      .format("HH:SS");
    const tz = moment.tz(data.cutoffTime, data.cutoffTimezone).zoneAbbr();

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    return (
      <>
        {lastUpdated && cutoffTime && (
          <Heading size={6} className="mb-4">
            Last updated {lastUpdated} {tz}.
            <br />
            Updated every day at {cutoffTime} {tz}.
          </Heading>
        )}

        {this.renderTable(data.data)}
      </>
    );
  }
}

export default SocialSummary;
